import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/apiClient";
import type { ShopifyCredentialStatus } from "../api/types";

function errText(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

/** Owner/admin only: the store connection. Linking and orders have their own pages. */
export function ShopifyPage() {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div className="page-header">
        <div>
          <h2 className="page-title">Shopify</h2>
          <p className="page-subtitle">
            Connect the web store. Link its products on{" "}
            <Link to="/app/shopify/products">Shopify products</Link>; web orders arrive on{" "}
            <Link to="/app/shopify-orders">Web orders</Link>.
          </p>
        </div>
      </div>
      <CredentialPanel />
    </section>
  );
}

function CredentialPanel() {
  const [status, setStatus] = useState<ShopifyCredentialStatus | null>(null);
  const [storeDomain, setStoreDomain] = useState("");
  const [apiVersion, setApiVersion] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const missingPickupScopes = ["read_locations", "read_merchant_managed_fulfillment_orders"].filter(
    (scope) => status?.connected && !status.scopes.includes(scope),
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      const next = await api.getShopifyCredentialStatus();
      setStatus(next);
      setStoreDomain(next.storeDomain ?? "");
      setApiVersion(next.apiVersion ?? "");
      setClientId(next.clientId ?? "");
    } catch (e) {
      setError(errText(e, "Could not load Shopify status"));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      setStatus(
        await api.setShopifyCredential({
          storeDomain,
          clientId,
          clientSecret,
          ...(apiVersion.trim() ? { apiVersion: apiVersion.trim() } : {}),
        }),
      );
      setClientSecret("");
    } catch (e) {
      setError(errText(e, "Could not connect Shopify"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ padding: "var(--space-4)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <div>
          <strong>{status?.connected ? `Connected to ${status.storeDomain}` : "Not connected"}</strong>
          {status?.connected ? (
            <p className="muted" style={{ margin: 0 }}>
              Permissions: {status.scopes.length > 0 ? status.scopes.join(", ") : "none reported"}
            </p>
          ) : (
            <ol className="muted" style={{ margin: "var(--space-1) 0 0", paddingLeft: "1.2em" }}>
              <li>In the Shopify Dev Dashboard, create an app with read_products, read_orders, read_locations and read_merchant_managed_fulfillment_orders. Add write_content for blog publishing.</li>
              <li>Release it and install it on your store.</li>
              <li>Paste the app&apos;s Client ID and Client secret here. The CRM renews its access itself.</li>
            </ol>
          )}
        </div>
        <div className="muted">{status?.lastOrderSyncAt ? `Last sync ${new Date(status.lastOrderSyncAt).toLocaleString()}` : "No sync yet"}</div>
      </div>
      {status?.blogScopeMissing ? (
        <div className="alert warning" style={{ marginTop: "var(--space-3)" }}>
          Orders and products work, but blog publishing needs the write_content permission on the Shopify app.
        </div>
      ) : null}
      {missingPickupScopes.length > 0 ? (
        <div className="alert warning" style={{ marginTop: "var(--space-3)" }}>
          Pickup routing needs {missingPickupScopes.join(" and ")}. Add these permissions to the Shopify app and update the connection before relying on web-order sync.
        </div>
      ) : null}
      {error ? <div className="alert error" style={{ marginTop: "var(--space-3)" }}>{error}</div> : null}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "var(--space-3)", marginTop: "var(--space-3)" }}>
        <div className="field">
          <label htmlFor="shopifyStoreDomain">Store domain</label>
          <input id="shopifyStoreDomain" value={storeDomain} onChange={(e) => setStoreDomain(e.target.value)} placeholder="myshop.myshopify.com" />
        </div>
        <div className="field">
          <label htmlFor="shopifyClientId">Client ID</label>
          <input id="shopifyClientId" value={clientId} onChange={(e) => setClientId(e.target.value)} autoComplete="off" />
        </div>
        <div className="field">
          <label htmlFor="shopifyClientSecret">Client secret</label>
          <input id="shopifyClientSecret" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} type="password" autoComplete="new-password" />
        </div>
        <div className="field">
          <label htmlFor="shopifyApiVersion">API version (optional)</label>
          <input id="shopifyApiVersion" value={apiVersion} onChange={(e) => setApiVersion(e.target.value)} placeholder="Leave empty for the default" />
        </div>
      </div>
      <div style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-3)" }}>
        <button type="button" className="button" disabled={saving || !storeDomain || !clientId || !clientSecret} onClick={() => void save()}>
          {saving ? "Checking with Shopify..." : status?.connected ? "Update credentials" : "Connect"}
        </button>
        {status?.connected ? (
          <button type="button" className="button danger" onClick={() => void api.disconnectShopify().then(setStatus)}>
            Disconnect
          </button>
        ) : null}
      </div>
    </div>
  );
}
